"use client";

import { useSearchParams } from "next/navigation";
import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  Suspense,
} from "react";
import { I18nextProvider } from "react-i18next";
import {
  i18n,
  loadPublicWidgetTranslations,
  loadTranslations,
} from "@/lib/i18n";

export const TranslatorContext = createContext({
  showTranslationKeys: false,
});

function TranslationProviderInner({
  children,
  namespaces = ["app"],
  locale = "en-GB",
  user_id,
  publicEmbedIdentifier,
  withoutUserId = false,
}) {
  const [loaded, setLoaded] = useState(false);
  const initializingRef = useRef(false);
  const searchParams = useSearchParams();
  const [showTranslationKeys, setShowTranslationKeys] = useState(false);

  useEffect(() => {
    const translatorParam = searchParams.get("translator");
    if (translatorParam !== null) {
      const isTranslator = translatorParam === "true";
      setShowTranslationKeys(isTranslator);
      localStorage.setItem("translator", String(isTranslator));
      return;
    }

    const storedValue = localStorage.getItem("translator");
    if (storedValue !== null) {
      setShowTranslationKeys(storedValue === "true");
    }
  }, [searchParams]);

  useEffect(() => {
    async function initializeTranslations() {
      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        await i18n.changeLanguage(locale);
        for (const namespace of namespaces) {
          if (publicEmbedIdentifier) {
            await loadPublicWidgetTranslations(
              locale,
              namespace,
              publicEmbedIdentifier
            );
          } else {
            await loadTranslations(locale, namespace, user_id);
          }
        }
        setLoaded(true);
      } catch (error) {
        console.error("Error initializing translations:", error);
      } finally {
        initializingRef.current = false;
      }
    }

    if (!loaded && (withoutUserId || user_id || publicEmbedIdentifier)) {
      initializeTranslations();
    }
  }, [
    locale,
    namespaces,
    user_id,
    publicEmbedIdentifier,
    withoutUserId,
    loaded,
  ]);

  if (!loaded) {
    return null; // or a loading indicator
  }

  return (
    <TranslatorContext.Provider value={{ showTranslationKeys }}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </TranslatorContext.Provider>
  );
}

export function TranslationProvider(props) {
  return (
    <Suspense fallback={null}>
      <TranslationProviderInner {...props} />
    </Suspense>
  );
}
